import { FC } from 'react';
import { useParams } from 'react-router';
import { useGetFcos } from '../../../api/fcos';
import { MaterializationStatusState } from '../../../api/gql/graphql';
import { useGetJobDetails } from '../../../api/jobs';
import { FeatureViewFCO, FeatureViewFCOType, WorkspaceFCOContainer } from '../../../core/types/fcoTypes';
import IdUtils from '../../../utils/id-utils';
import { secondsToHumanFriendlyString } from '../../../utils/misc-utils';
import { typeLabelMap } from './FeatureViewTypeBadge';
import { Badge, Card, ColumnLayout, FlexGroup, FlexItem, Link, Empty, Loading, KeyValueList } from '@lithos';
import { JobStatusBadge } from '@tecton';
import { TaskType } from '../../../types/tecton_proto/materialization/spark_cluster';
import { taskTypeLabelMap } from './TaskTypeBadge';
import { TaskAttemptDetails } from '../../../types/tecton_proto/metadataservice/metadata_service';

interface ImplicitConfigClusterCardProps {
  attemptDetails?: TaskAttemptDetails;
}

const ImplicitConfigClusterCard: FC<ImplicitConfigClusterCardProps> = ({ attemptDetails }) => {
  if (!attemptDetails) {
    return (
      <Card>
        <Empty
          title={'Cluster Configuration'}
          description={<>Not Available: Feature View Not Applied</>}
          placement="embedded"
          useCase="errorKnown"
          actions={undefined}
        />
      </Card>
    );
  }

  const { cluster_config, environment } = attemptDetails;

  const items = [
    {
      title: 'Instance Type',
      content: cluster_config?.instance_type ?? <>No Instance Type Specified</>,
    },
    {
      title: 'Environment',
      content: environment ?? <>No Environment Specified</>,
    },
  ];

  return (
    <Card title="Cluster Configuration">
      <KeyValueList items={items} />
    </Card>
  );
};

const JobDetailMetadata: FC = () => {
  const { jobId } = useParams();
  const { data: jobDetailData, isSuccess: materializationStatusIsSuccess } = useGetJobDetails(jobId ?? '');

  const { data: featureViewData, isSuccess: featureViewIsSuccess } = useGetFcos<FeatureViewFCO>(
    jobDetailData?.fco_locator?.workspace ?? '',
    {
      enabled: materializationStatusIsSuccess, // we wait for the Job Details request to finish since we need the workspace and FV name
      select: (data: WorkspaceFCOContainer) => {
        return data?.featureViews.find((fv) => {
          const fcoId = IdUtils.toStringId(jobDetailData?.fco_locator?.id);
          // Let's find the feature view from this workspace
          return fv?.id === fcoId;
        });
      },
    }
  );

  if (!materializationStatusIsSuccess || !featureViewIsSuccess) {
    return (
      <>
        <Loading />
      </>
    );
  }

  const attemptStatus = jobDetailData?.attempt_details?.attempt_status;
  const workspaceName = jobDetailData?.fco_locator?.workspace;

  // Replace these with actual values
  const jobStatus =
    attemptStatus?.materialization_state ?? MaterializationStatusState.MaterializationStatusStateUnspecified;
  const jobType = taskTypeLabelMap[jobDetailData?.attempt_details?.task_type ?? TaskType.UNKNOWN];

  const durationInSeconds = parseInt(attemptStatus?.duration?.toString() ?? '0');

  const materializationWindowStart = attemptStatus?.window_start_time ?? '';
  const materializationWindowEnd = attemptStatus?.window_end_time ?? '';

  // TODO: delete this and when we migrate this page over to graphql
  const jobDetailAttributes = [
    {
      title: 'Job Status',
      content: <>{jobStatus ? <JobStatusBadge status={jobStatus as MaterializationStatusState} /> : <>-</>}</>,
    },
    {
      title: 'Job Type',
      content: jobType,
    },
    { title: 'Duration', content: secondsToHumanFriendlyString(durationInSeconds) },
    {
      title: 'Feature View Name',
      content: (
        <>
          {featureViewData ? (
            <Link
              url={`/repo/${featureViewData.workspace}/features/${featureViewData?.name}`}
              icon={'FeatureView'}
              name={featureViewData.name}
            />
          ) : (
            <>Not Available: Feature View Not Applied</>
          )}
        </>
      ),
    },
    {
      title: 'Feature View Type',
      content: typeLabelMap[(featureViewData?.featureViewType as FeatureViewFCOType) ?? FeatureViewFCOType.UNKNOWN],
    },
    {
      title: 'Materialization Window Start',
      content: materializationWindowStart,
    },
    {
      title: 'Materialization Window End',
      content: materializationWindowEnd,
    },
    {
      title: 'Online Materialization',
      content: (
        <Badge
          label={featureViewData?.isOnlineMaterializationEnabled ? 'Enabled' : 'Disabled'}
          color={featureViewData?.isOnlineMaterializationEnabled ? 'blue' : 'gray'}
          size="s"
        />
      ),
    },
    {
      title: 'Offline Materialization',
      content: (
        <Badge
          label={featureViewData?.isOfflineMaterializationEnabled ? 'Enabled' : 'Disabled'}
          color={featureViewData?.isOfflineMaterializationEnabled ? 'blue' : 'gray'}
          size="s"
        />
      ),
    },
    {
      title: 'Rift Job',
      content: jobDetailData.attempt_details?.rift_instance_url ? (
        <Link url={jobDetailData.attempt_details?.rift_instance_url} name={'Job Detail'} />
      ) : (
        <>n/a</>
      ),
    },
    {
      title: 'Workspace',
      content: workspaceName ? <>{workspaceName}</> : <>n/a</>,
    },
  ];

  return (
    <>
      <FlexGroup gapSize="4" direction="column" padding="0">
        <FlexItem>
          <ColumnLayout>
            <Card title="Job Details">
              <KeyValueList items={jobDetailAttributes} />
            </Card>
            <ImplicitConfigClusterCard attemptDetails={jobDetailData?.attempt_details} />
          </ColumnLayout>
        </FlexItem>
      </FlexGroup>
    </>
  );
};

export default JobDetailMetadata;
